// contracts.jsx — the EW-012 async-state contract components every block editor
// shares: a consistent Loading, Empty, and Error surface so no editor invents its
// own (the EW-012 finding = "editors had no empty/error/loading contracts").
// WL-011 (Fable Hail Mary Wave 2).
//
// Accessibility (WL-027): loading announces via role=status/aria-busy; error
// uses role=alert; retry is a real >=44px button; line-art icons (no emoji).

import React from "react";
import styled from "styled-components";
import { fontFamily, brand, focusRing } from "../../../theme/tokens";
import { fieldChrome } from "./EditorField";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const PURPLE = brand.primaryActive; // #8F38EF — the darkened primary (AA on white)

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  padding: 32px 16px;
  font-family: ${fontFamily};
  color: ${MUTED};
`;

const Heading = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${INK};
`;

const Copy = styled.div`
  font-size: 12px;
  line-height: 1.5;
  max-width: 240px;
`;

const RetryBtn = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  background: ${PURPLE};
  color: #fff;
  font-family: ${fontFamily};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  &:hover { background: #7c2fd6; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// ── Loading — a skeleton of the control stack (not a bare spinner) ────────────
const SkelRow = styled.div`
  height: ${(p) => p.$h || 40}px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f2f2f4 25%, #e9e9ee 37%, #f2f2f4 63%);
  background-size: 400% 100%;
  animation: iy-shimmer 1.4s ease infinite;
  @keyframes iy-shimmer { 0% { background-position: 100% 0; } 100% { background-position: -100% 0; } }
  @media (prefers-reduced-motion: reduce) { animation: none; background: #f2f2f4; }
`;

const SkelWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 2px;
`;

export const EditorLoading = ({ rows = 4 }) => (
  <SkelWrap role="status" aria-busy="true" aria-live="polite">
    <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
      Loading settings
    </span>
    {Array.from({ length: rows }).map((_, i) => (
      <SkelRow key={i} $h={i === 0 ? 24 : 40} aria-hidden="true" />
    ))}
  </SkelWrap>
);

// ── Empty — nothing selected / no content yet ────────────────────────────────
const InboxIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 13h4l2 3h6l2-3h4" stroke={PURPLE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 6h14l2 7v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5l2-7z" stroke={PURPLE} strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

export const EditorEmpty = ({ blockName = "block", title, copy, action }) => (
  <Center>
    <InboxIcon />
    <Heading>{title || "Nothing to configure yet"}</Heading>
    <Copy>{copy || `Add content to this ${String(blockName).toLowerCase()} to reveal its settings.`}</Copy>
    {action}
  </Center>
);

// ── Error — load/save failed, with a retry path ──────────────────────────────
const AlertIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 9v4" stroke="#D92D20" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="1" fill="#D92D20" />
    <path d="M10.3 3.9 2.7 17a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" stroke="#D92D20" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

export const EditorError = ({ message, onRetry }) => (
  <Center role="alert">
    <AlertIcon />
    <Heading>Couldn&apos;t load these settings</Heading>
    <Copy>{message || "Something went wrong. Your work is safe — try loading the settings again."}</Copy>
    {onRetry && (
      <RetryBtn type="button" onClick={onRetry}>
        Try again
      </RetryBtn>
    )}
  </Center>
);

export default { EditorLoading, EditorEmpty, EditorError };
